/**
 * ACCESOS: LA HOJA DE PAGOS Y STRIPE, CADA NOCHE (Francisco, 28-09).
 *
 * El backend (core/acceso_automatico) cruza cada noche a las 04:30 la hoja «Control de Pagos»
 * y Stripe (solo lectura) con las fichas, y deja un parte. Aquí se ve ese parte, se puede
 * revisar ahora y se elige el modo: «Solo parte» (de fábrica, no toca nada) o «Aplicar»
 * (escribe los cambios). Nació con Benito Velasco: pagó el 21-09 y el 28-09 la app le decía
 * que su suscripción había terminado.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ShieldCheck, AlertTriangle } from 'lucide-react';

const ACCION = {
    dar_acceso: 'Recupera el acceso',
    quitar_acceso: 'Se le quita el acceso',
    actualizar: 'Se corrigen sus fechas o su plan',
};

// Lo que solo rellena el segundo campo de la fecha no cambia nada de lo que ve el cliente
// hoy: se enseña aparte para que lo importante no se pierda en la lista.
const soloFecha = (c) => c.accion === 'actualizar' && Object.keys(c.despues || {}).every((k) => k === 'access_until');

const CAMPO = {
    status: 'estado', plan: 'plan', current_period_end: 'fin del ciclo', access_until: 'acceso hasta',
    pago_pendiente_hoja: 'cobro pendiente', fecha_baja: 'baja', motivo_baja: 'motivo',
};
// Las fechas, solo el día; lo demás, entero.
const valor = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (v === true) return 'sí';
    if (v === false) return 'no';
    const t = String(v);
    return /^\d{4}-\d{2}-\d{2}/.test(t) ? t.slice(0, 10) : t;
};

const fechaCorta = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const AdminAccesosPage = () => {
    const { api } = useAuth();
    const [estado, setEstado] = useState(null);
    const [trabajando, setTrabajando] = useState(false);
    const [verTodos, setVerTodos] = useState(false);

    const cargar = () => api.get('/admin/acceso-automatico')
        .then((res) => setEstado(res.data))
        .catch((e) => { console.error('[accesos]', e); toast.error('No se pudo cargar el parte de accesos'); });

    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al entrar
    useEffect(() => { cargar(); }, []);

    const revisar = async (aplicar) => {
        if (aplicar && !window.confirm('Se van a escribir los cambios en las fichas de los clientes. ¿Seguimos?')) return;
        setTrabajando(true);
        try {
            const res = await api.post('/admin/acceso-automatico/revisar', { aplicar });
            setEstado((prev) => ({ ...(prev || {}), ultimo_parte: res.data }));
            toast.success(aplicar ? `Aplicado: ${res.data.cambios.length} cambios` : `Revisado: ${res.data.cambios.length} cambios propuestos`);
        } catch (e) {
            console.error('[accesos] revisar', e);
            toast.error('No se pudo revisar ahora. Inténtalo en un rato.');
        } finally {
            setTrabajando(false);
        }
    };

    const cambiarModo = async (modo) => {
        if (modo === 'aplicar' && !window.confirm('Cada noche se escribirán los cambios en las fichas sin preguntar. ¿Seguimos?')) return;
        try {
            const res = await api.put('/admin/acceso-automatico/modo', { modo });
            setEstado((prev) => ({ ...(prev || {}), modo: res.data.modo }));
            toast.success(res.data.modo === 'aplicar' ? 'Cada noche se aplicarán los cambios' : 'Cada noche solo se hará el parte');
        } catch (e) {
            console.error('[accesos] modo', e);
            toast.error('No se pudo cambiar el modo');
        }
    };

    const parte = estado?.ultimo_parte;
    const cambios = useMemo(() => parte?.cambios || [], [parte]);
    const importantes = cambios.filter((c) => !soloFecha(c));
    const deFecha = cambios.filter(soloFecha);

    return (
        <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-5" data-testid="admin-accesos">
            <div className="flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-brand" />
                <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase">Accesos</h1>
            </div>

            <Card>
                <CardContent className="p-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-2" data-testid="accesos-modo">
                        <span className="text-sm font-semibold mr-1">Cada noche:</span>
                        {[['parte', 'Solo el parte'], ['aplicar', 'Aplicar los cambios']].map(([m, texto]) => (
                            <button key={m} type="button" onClick={() => estado?.modo !== m && cambiarModo(m)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${estado?.modo === m ? 'bg-brand text-white border-brand' : 'border-border text-muted-foreground hover:border-brand/50'}`}>
                                {texto}
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Stripe: {estado?.stripe_configurado ? 'conectado' : 'sin conectar'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <Button onClick={() => revisar(false)} disabled={trabajando} variant="outline" data-testid="accesos-revisar">
                            Revisar ahora
                        </Button>
                        <Button onClick={() => revisar(true)} disabled={trabajando} data-testid="accesos-aplicar">
                            Aplicar ahora
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {!parte ? (
                <p className="text-sm text-muted-foreground">Todavía no hay ningún parte. Pulsa «Revisar ahora».</p>
            ) : (
                <>
                    <p className="text-sm text-muted-foreground" data-testid="accesos-resumen">
                        Último parte: {fechaCorta(parte.creado)} · {parte.aplicado ? 'aplicado' : 'solo parte, sin cambios en las fichas'}
                        {parte.hoja ? ` · hoja: ${parte.hoja.activos} activos y ${parte.hoja.bajas} bajas` : ''}
                        {parte.stripe && !parte.stripe.leido ? ` · ${parte.stripe.motivo}` : ''}
                    </p>
                    {(parte.errores || []).map((e) => (
                        <p key={e} className="text-sm text-red-400 flex gap-2"><AlertTriangle className="w-4 h-4 mt-0.5" /> {e}</p>
                    ))}

                    {(parte.avisos || []).length > 0 && (
                        <section className="space-y-2" data-testid="accesos-avisos">
                            <p className="caption">Para mirar ({parte.avisos.length})</p>
                            {parte.avisos.map((a, i) => (
                                <div key={i} className="surface p-3 text-sm">
                                    <p className="font-semibold">{a.nombre} <span className="text-muted-foreground font-normal">{a.correo}</span></p>
                                    <p className="text-muted-foreground mt-0.5">{a.texto}</p>
                                </div>
                            ))}
                        </section>
                    )}

                    <section className="space-y-2" data-testid="accesos-cambios">
                        <p className="caption">{parte.aplicado ? 'Cambios hechos' : 'Cambios propuestos'} ({importantes.length})</p>
                        {importantes.length === 0 && <p className="text-sm text-muted-foreground">Nada que cambiar en el acceso de nadie.</p>}
                        {importantes.map((c, i) => (
                            <div key={i} className="surface p-3 text-sm">
                                <p className="font-semibold">{ACCION[c.accion] || c.accion}: {c.nombre} <span className="text-muted-foreground font-normal">{c.correo}</span></p>
                                <p className="text-muted-foreground mt-0.5">{c.motivo}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {Object.entries(c.despues || {}).map(([k, v]) => `${CAMPO[k] || k}: ${valor(c.antes?.[k])} → ${valor(v)}`).join(' · ')}
                                </p>
                            </div>
                        ))}
                        {deFecha.length > 0 && (
                            <button type="button" onClick={() => setVerTodos((v) => !v)} className="text-sm text-brand font-semibold">
                                {verTodos ? 'Ocultar' : 'Ver'} {deFecha.length} fichas a las que solo se les iguala la fecha de acceso
                            </button>
                        )}
                        {verTodos && deFecha.map((c, i) => (
                            <p key={i} className="text-xs text-muted-foreground">{c.nombre} ({c.correo}): hasta el {String(c.despues.access_until).slice(0, 10)}</p>
                        ))}
                    </section>

                    {(parte.sin_cuenta || []).length > 0 && (
                        <section className="space-y-1">
                            <p className="caption">En la hoja o en Stripe y sin cuenta en la app ({parte.sin_cuenta.length})</p>
                            {parte.sin_cuenta.map((s) => (
                                <p key={s.correo} className="text-sm text-muted-foreground">{s.nombre} · {s.correo} · {s.membresia}</p>
                            ))}
                        </section>
                    )}
                </>
            )}
        </div>
    );
};

export default AdminAccesosPage;
